import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * Used to remove document and summary click listeners when the component disconnects.
   * @type {AbortController}
   */
  #abortController = new AbortController();

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    // Close any open details when clicking outside
    document.addEventListener('click', this.#handleClickOutside, {
      signal: this.#abortController.signal,
    });

    // Handle closing animation when summary is clicked
    /** @type {WeakMap<HTMLDetailsElement, boolean>} */
    const drawerSubmenuWasOpen = new WeakMap();

    this.querySelectorAll('details.drawer-submenu summary').forEach((summary) => {
      summary.addEventListener(
        'pointerdown',
        () => {
          const details = summary.closest('details.drawer-submenu');
          if (details instanceof HTMLDetailsElement) {
            drawerSubmenuWasOpen.set(details, details.hasAttribute('open'));
          }
        },
        { signal: this.#abortController.signal }
      );

      summary.addEventListener('click', (e) => {
        const details = summary.closest('details.drawer-submenu');
        if (!(details instanceof HTMLDetailsElement)) return;

        const wasOpen = drawerSubmenuWasOpen.get(details) ?? false;
        drawerSubmenuWasOpen.delete(details);

        // If already open before this gesture, intercept the close and animate it.
        // header-drawer/open sets [open] in capture before this bubble handler runs.
        if (
          wasOpen &&
          !summary.classList.contains('accordion-false') &&
          !(e.target instanceof Element && e.target.closest('a'))
        ) {
          e.preventDefault();
          this.#closeDetailsWithAnimation(details);
        } else {
          // If opening this menu, close all other open menus first
          const otherOpenDetails = this.querySelectorAll('details.drawer-submenu[open]');
          otherOpenDetails.forEach((otherDetails) => {
            if (otherDetails !== details && otherDetails instanceof HTMLDetailsElement) {
              this.#closeDetailsWithAnimation(otherDetails);
            }
          });
          document.body.classList.add('mega-menu-open');
        }
      }, {
        signal: this.#abortController.signal,
      });
    });

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    document.body.classList.remove('mega-menu-open');
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Handle clicks outside the header to close open menus
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if click is outside the header
    const header = target.closest('header, header-menu');
    if (!header) {
      // Close all open details in this menu with animation
      const openDetails = this.querySelectorAll('details.drawer-submenu[open]');
      openDetails.forEach((details) => {
        if (details instanceof HTMLDetailsElement) {
          this.#closeDetailsWithAnimation(details);
        }
      });
    }
  };

  /**
   * Close a drawer-submenu details element with closing animation.
   * Adds closing class, updates aria and menu-open, then removes open/closing after the animation delay.
   * @param {HTMLDetailsElement} details
   */
  #closeDetailsWithAnimation(details) {
    if (!(details instanceof HTMLDetailsElement)) return;

    const summary = details.querySelector('summary');
    details.classList.add('closing');
    details.classList.remove('menu-open');
    summary?.setAttribute('aria-expanded', 'false');

    const animationDuration =
      parseInt(getComputedStyle(this).getPropertyValue('--submenu-animation-speed'), 10) || 200;
    setTimeout(() => {
      details.removeAttribute('open');
      details.classList.remove('closing');
      if (this.querySelectorAll('details.drawer-submenu[open]').length === 0) {
        document.body.classList.remove('mega-menu-open');
      }
    }, animationDuration);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
